/**
 * useNotifications — F-H.2
 *
 * TanStack Query Hook für GET /api/v2/notifications
 * Graceful degradation bei 404 (Endpoint noch nicht implementiert).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '../../lib/api-client'
import { ApiError } from '../../lib/errors/ApiError'

// --- Lokale Typen (bis B-Notifications in api-types landet) ---

export interface Notification {
  id: number
  event_type: string // 'reward' | 'offline' | 'online' | 'beacon' | 'witness' | 'payment'
  message: string
  title?: string
  created_at: string
  read_at: string | null
}

export interface NotificationsResponse {
  data: Notification[]
  meta?: {
    page: number
    per_page: number
    total_count: number
    has_more: boolean
  }
}

export type NotificationFilter = 'all' | 'unread' | 'alerts' | 'rewards' | 'bans' | 'watched'

// --- API-Calls ---

// Beide Pfade fehlen im @heliumtracker/api-types-Contract — etabliertes
// Cast-Muster (siehe useHotspotsMapData.ts/useSubscription.ts) statt `any`.
type UntypedGet = (
  url: string,
  opts: { params: { query: Record<string, unknown> } },
) => Promise<{ data?: unknown; error?: unknown }>
type UntypedPost = (url: string) => Promise<{ data?: unknown; error?: unknown }>

async function fetchNotificationsRaw(unread: boolean): Promise<NotificationsResponse | null> {
  try {
    const untypedGet = apiClient.GET as unknown as UntypedGet
    const { data } = await untypedGet('/api/v2/notifications', {
      params: {
        query: {
          page: 1,
          unread: unread ? true : undefined
        }
      }
    })
    return data as NotificationsResponse
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return null
    }
    throw error
  }
}

async function markAllReadRaw(): Promise<void> {
  try {
    const untypedPost = apiClient.POST as unknown as UntypedPost
    await untypedPost('/api/v2/notifications/mark_all_read')
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      throw new Error('404', { cause: error })
    }
    throw error
  }
}

// --- Query Key ---

export const NOTIFICATIONS_QUERY_KEY = (unread: boolean) =>
  ['notifications', { unread }] as const

// --- Hooks ---

export function useNotifications(filter: NotificationFilter) {
  const unread = filter === 'unread'

  return useQuery({
    queryKey: NOTIFICATIONS_QUERY_KEY(unread),
    queryFn: () => fetchNotificationsRaw(unread),
    staleTime: 30_000,
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('404')) return false
      return failureCount < 2
    },
  })
}

export function useMarkAllRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: markAllReadRaw,
    onSuccess: () => {
      // Invalidate all notification queries
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })
}
